<template>
  <div v-if="isOpen && file">
    <Modal
      color-variation="abstract"
      :show-footer="false"
      :isCropper="true"
      @hideModal="closeModal"
    >
      <template #header>
        <h2 class="header">Crop Image</h2>
      </template>

      <template #body>
        <div class="holder">
          <cropper
            :src="img"
            @change="onChange"
            ref="cropper"
            class="twitter-cropper"
            background-class="twitter-cropper__background"
            foreground-class="twitter-cropper__foreground"
            image-restriction="stencil"
            :stencil-props="{
              lines: {},
              handlers: {},
              movable: false,
              scalable: false,
              aspectRatio: aspectRatio,
              previewClass: 'twitter-cropper__stencil'
            }"
            :default-size="defaultSize"
            :stencil-size="stencilSize"
            :transitions="true"
            :canvas="true"
            :debounce="false"
            :min-width="150"
            :min-height="150"
          />

          <VerticalButtons @flip="flip" @rotate="rotate" />
        </div>

        <!-- Zoom Action -->
        <section class="zoom-region">
          <!-- Zoom Action -->
          <Navigation :zoom="zoom" @change="onZoom" />
        </section>

        <section class="actions">
          <button
            class="whitebutton-gemnote edit-whitebutton-gemnote--small"
            @click="reset"
          >
            <span>Reset</span>
          </button>

          <button
            class="button-gemnote button-gemnote--small"
            @click="cropImage"
          >
            <span> Save </span>
          </button>
        </section>
      </template>
    </Modal>
  </div>
</template>

<script>
import { Cropper } from 'vue-advanced-cropper'
import Modal from '@/components/Universal/Modal'
import 'vue-advanced-cropper/dist/style.css'
import 'vue-advanced-cropper/dist/theme.classic.css'
import i18n from '../../../internatinalization/i18n'
import VerticalButtons from './VerticalButton'
import Navigation from './Navigation'

export default {
  components: { Cropper, Modal, VerticalButtons, Navigation },

  props: {
    isOpen: {
      type: Boolean,
      default: false
    },

    image: {
      type: Event,
      default: null
    },

    aspectRatio: {
      type: Number,
      default: 1 / 1 //   Value could be 16 /9
    }
  },

  watch: {
    image: {
      handler() {
        if (this.file) this.setImage(this.image)
      },
      immediate: true
    }
  },

  data: () => ({
    zoom: 0,
    img: '',
    resultImg: '',
    coordinates: {}
  }),

  computed: {
    file() {
      const file = this.image && this.image.target.files[0]
      return file
    }
  },

  methods: {
    cropImage() {
      const { canvas } = this.$refs.cropper.getResult()

      if (canvas) {
        canvas.toBlob(blob => {
          blob.name = this.file.name
          this.$emit('crop', blob)
          this.closeModal()
        }, 'image/jpeg')
      }
    },

    setImage(e) {
      const file = e.target.files[0]
      let { size } = file
      size = Math.round(size / 1024)

      if (file.type.indexOf('image/') === -1) {
        this.$store.dispatch('notification/pushNotification', {
          type: 'info',
          messages: ['Please select an image file']
        })
        return
      }

      if (size >= 5048) {
        this.$store.dispatch('notification/pushNotification', {
          type: 'error',
          messages: [i18n.t('validations.image-size-5MB-exceeded')]
        })
        return
      }

      const blob = URL.createObjectURL(file)
      this.img = blob
    },

    stencilSize({ boundaries }) {
      const rounded = Math.ceil(this.aspectRatio)
      if (rounded == 2) {
        return {
          width: 600,
          height: Math.min(boundaries.height, boundaries.width) - 48
        }
      } else {
        return {
          width: 300,
          height: 400
        }
      }
    },

    defaultSize({ imageSize }) {
      return {
        width: Math.min(imageSize.height, imageSize.width),
        height: Math.min(imageSize.height, imageSize.width)
      }
    },

    onChange(result) {
      const { coordinates, canvas } = result
      this.coordinates = coordinates
      this.resultImg = canvas.toDataURL()

      const cropper = this.$refs.cropper
      if (cropper) {
        const { coordinates, imageSize } = cropper
        if (
          imageSize.width / imageSize.height >
          coordinates.width / coordinates.height
        ) {
          // Determine the position of slider bullet
          // It's 0 if the stencil has the maximum size and it's 1 if the has the minimum size
          this.zoom =
            (cropper.imageSize.height - cropper.coordinates.height) /
            (cropper.imageSize.height - cropper.sizeRestrictions.minHeight)
        } else {
          this.zoom =
            (cropper.imageSize.width - cropper.coordinates.width) /
            (cropper.imageSize.width - cropper.sizeRestrictions.minWidth)
        }
      }
    },

    onZoom(value) {
      const cropper = this.$refs.cropper
      if (cropper) {
        if (cropper.imageSize.height < cropper.imageSize.width) {
          const minHeight = cropper.sizeRestrictions.minHeight
          const imageHeight = cropper.imageSize.height
          // Determine the current absolute zoom and the new absolute zoom
          // to calculate the sought relative zoom value
          cropper.zoom(
            (imageHeight - this.zoom * (imageHeight - minHeight)) /
              (imageHeight - value * (imageHeight - minHeight))
          )
        } else {
          const minWidth = cropper.sizeRestrictions.minWidth
          const imageWidth = cropper.imageSize.width
          cropper.zoom(
            (imageWidth - this.zoom * (imageWidth - minWidth)) /
              (imageWidth - value * (imageWidth - minWidth))
          )
        }
      }
    },

    flip(x, y) {
      const { image } = this.$refs.cropper.getResult()
      if (image.transforms.rotate % 180 !== 0) {
        this.$refs.cropper.flip(!x, !y)
      } else {
        this.$refs.cropper.flip(x, y)
      }
    },

    rotate(angle) {
      this.$refs.cropper.rotate(angle)
    },

    closeModal() {
      this.$emit('closeModal')
    },

    reset() {
      this.$refs.cropper.reset()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/global.scss';
.header {
  font-family: $editorial;
  font-weight: normal;
  color: #25282a;
  text-align: center;
}

.holder {
  position: relative;
  margin-top: 1.25rem;
  background: #f5f2ee;
  padding: 1.5rem 3rem;
  height: 25.5rem;
  overflow: hidden;
}

.zoom-region {
  display: flex;
  justify-content: center;
  margin-top: 0.5rem;
}

.actions {
  display: flex;
  justify-content: center;
  margin-top: 1.25rem;

  button {
    margin-bottom: 2rem;
    height: 3.25rem;
    width: auto;
    font-size: 1rem;
    padding: 0 4.5rem !important ;

    &:nth-child(1) {
      margin-right: 0.875rem;

      &:hover {
        background: inherit;
      }
    }
  }
}
</style>
